import {
  QUnpassedErrorLevelRisksOverTimeCard,
  QAppVendorRiskAssessmentCard,
  QTopAppRisksCard,
  QMostRiskAppsCard,
  QMostErrorLevelRisksAppVersionsCard,
} from '@kw/quokka-ui';
import { Box } from '@mui/material';
import { DashboardAnalyticsDateRangeDays, DashboardAnalyticsMetrics } from './dashboardAnalytics';
import { useDashboardNavigation } from './useDashboardNavigation';

type RiskInsightsProps = {
  dateRangeDays: DashboardAnalyticsDateRangeDays;
  metrics: DashboardAnalyticsMetrics | null | undefined;
};

const platforms = ['android', 'ios'] as const;

export const RiskInsights = ({ dateRangeDays, metrics }: RiskInsightsProps) => {
  const { getApplicationsHref, getAppReportHref } = useDashboardNavigation(dateRangeDays);
  const unpassedErrorLevelRisksOverTime = metrics?.unpassedErrorLevelRisksOverTime ?? [];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: '24px',
        '& .MuiPaper-root': { width: '100%' },
      }}
    >
      <Box sx={{ gridColumn: 'span 6', height: 400 }}>
        <QUnpassedErrorLevelRisksOverTimeCard
          data-testid="unpassed-error-level-risk-over-time-card"
          data={unpassedErrorLevelRisksOverTime}
          sx={{ width: '100%', height: '100%' }}
        />
      </Box>

      {/* Row 1: Top App Risks — Android + iOS side-by-side */}
      {platforms.map(platform => (
        <Box key={`risks-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QTopAppRisksCard
            platform={platform}
            data={(platform === 'android' ? metrics?.topAppRisksAndroid : metrics?.topAppRisksIos) ?? []}
            getRiskHref={item => getApplicationsHref({ platform, riskName: item.riskName, riskSeverity: 'error' })}
          />
        </Box>
      ))}

      {/* Row 2: Most Error-Level Risks App Versions — Android + iOS side-by-side */}
      {platforms.map(platform => (
        <Box key={`error-versions-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QMostErrorLevelRisksAppVersionsCard
            platform={platform}
            data={
              (platform === 'android' ? metrics?.mostErrorLevelRisksAppVersionsAndroid : metrics?.mostErrorLevelRisksAppVersionsIos) ?? []
            }
            getViewReportHref={item => getAppReportHref(item.analysisId)}
          />
        </Box>
      ))}

      {/* Row 3: Most Risk Apps — Android + iOS side-by-side */}
      {platforms.map(platform => (
        <Box key={`most-risk-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QMostRiskAppsCard
            platform={platform}
            data={(platform === 'android' ? metrics?.mostRiskAppsAndroid : metrics?.mostRiskAppsIos) ?? []}
            getAppNameHref={item => getApplicationsHref({ platform, searchQuery: item.applicationId })}
          />
        </Box>
      ))}

      {/* Row 4: Vendor Risk Assessment — Android + iOS side-by-side */}
      {platforms.map(platform => (
        <Box key={`vendor-${platform}`} sx={{ gridColumn: 'span 3' }}>
          <QAppVendorRiskAssessmentCard
            platform={platform}
            data={(platform === 'android' ? metrics?.vendorRiskAssessmentAndroid : metrics?.vendorRiskAssessmentIos) ?? []}
            getVendorHref={item => getApplicationsHref({ platform, vendorName: item.vendor })}
          />
        </Box>
      ))}
    </Box>
  );
};
